import { isNativeWebView, shouldHintIphoneFullscreen } from '@polygraf/shared'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const STORAGE_KEY = 'polygraf.iphoneFullscreenHintSeen'
/** Kept at 20 s: the strip sits in the layout and covers nothing, so a short timeout would only cost slow readers (WCAG 2.2.1). Also dismissible. */
const AUTO_HIDE_MS = 20_000

function hintApplies(): boolean {
	const nav = globalThis.navigator as (Navigator & { standalone?: boolean }) | undefined
	if (!nav) return false
	return shouldHintIphoneFullscreen({
		userAgent: nav.userAgent,
		navigatorStandalone: nav.standalone,
		displayModeStandalone:
			globalThis.matchMedia?.('(display-mode: standalone), (display-mode: fullscreen)').matches ??
			false,
		nativeWebView: isNativeWebView(),
	})
}

function wasSeen(): boolean {
	try {
		return globalThis.localStorage.getItem(STORAGE_KEY) === '1'
	} catch {
		return false
	}
}

function markSeen(): void {
	try {
		globalThis.localStorage.setItem(STORAGE_KEY, '1')
	} catch {
		// Private mode: the hint may show again next time, which is harmless.
	}
}

/**
 * One-time note for iPhone browsers: the fullscreen button hid the viewer's own
 * controls, but the browser's address bar and toolbar cannot be hidden by a page.
 * Shown the first time `active` (immersive mode) turns on, never in the app
 * WebView, on iPad or in a Home Screen launch.
 *
 * It is an in-flow strip, the first child of the viewer's flex column, taking
 * the place of the header that immersive mode hides. Floating over the text
 * covered the whole visible transcript on a 390 px phone (top edge: the oldest
 * lines, which are all there is in a short transcript; bottom edge: the live
 * edge the reader is following), so the text area is made shorter instead.
 * Every route that renders this keeps the header out of immersive mode.
 */
export function IphoneFullscreenHint({ active }: { active: boolean }) {
	const { t } = useTranslation()
	const [visible, setVisible] = useState(false)

	useEffect(() => {
		if (!active || wasSeen() || !hintApplies()) return
		markSeen()
		setVisible(true)
	}, [active])

	useEffect(() => {
		if (!visible) return
		const timer = setTimeout(() => setVisible(false), AUTO_HIDE_MS)
		return () => clearTimeout(timer)
	}, [visible])

	// Leaving immersive mode brings the toolbar back; the hint has done its job.
	useEffect(() => {
		if (!active) setVisible(false)
	}, [active])

	if (!visible) return null

	return (
		<output
			className="flex shrink-0 items-center gap-2 border-b border-base-content/20 bg-base-100 px-3 py-1 text-sm text-base-content"
			style={{ paddingTop: 'max(0.25rem, env(safe-area-inset-top))' }}
		>
			<p className="flex-1 leading-snug">{t('viewer.iphoneFullscreenHint.text')}</p>
			<button
				type="button"
				className="btn btn-ghost btn-sm btn-square"
				aria-label={t('viewer.iphoneFullscreenHint.dismiss')}
				onClick={() => setVisible(false)}
			>
				<X size={16} aria-hidden="true" />
			</button>
		</output>
	)
}
